import React, { useEffect, useState } from 'react';
import { DefaultService } from '../generated-api';
import { isOptedOut, setOptOut, startTelemetry } from '../services/telemetry';

const PrivacyPage: React.FC = () => {
  const [optedOut, setOptedOut] = useState<boolean>(isOptedOut());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    DefaultService.getTelemetryPreferences()
      .then(prefs => setOptedOut(prefs.opted_out))
      .catch(() => undefined);
  }, []);

  const toggle = async () => {
    const next = !optedOut;
    setSaving(true);
    setError(null);
    // Stop or resume locally right away; the server copy makes it stick across devices.
    setOptOut(next);
    if (!next) startTelemetry();
    setOptedOut(next);
    try {
      await DefaultService.updateTelemetryPreferences({ requestBody: { opted_out: next } });
    } catch {
      setError('Saved on this device only; the server could not be reached.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-6 space-y-4">
      <h1 className="text-2xl font-bold text-heading">Privacy</h1>
      <div className="bg-surface rounded-lg border border-edge p-4 space-y-2 text-sm text-body">
        <p>
          To learn how Board Game Stats is used, we record page views, clicks, errors and how fast pages
          load, plus session replays (a recording of what the page looked like while you used it). Everything
          you type into fields is masked in replays and passwords are never recorded.
        </p>
        <p>
          The data is tied to your login, stays in our own AWS account (no third-party analytics), is only
          visible to the site admin, and is deleted automatically: replays after 90 days, events after about
          13 months.
        </p>
      </div>
      <div className="bg-surface rounded-lg border border-edge p-4 flex items-center justify-between">
        <div>
          <div className="font-medium text-heading">Record my usage and session replays</div>
          <div className="text-xs text-muted">{optedOut ? 'Off: nothing is recorded for you.' : 'On'}</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!optedOut}
          data-track="telemetry_opt_toggle"
          data-testid="telemetry-opt-toggle"
          disabled={saving}
          onClick={toggle}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            optedOut ? 'bg-gray-300' : 'bg-primary-500'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              optedOut ? 'translate-x-0.5' : 'translate-x-5'
            }`}
          />
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default PrivacyPage;
